import React from "react";
import { Box, Button, Card, Chip, Container, Grid, Stack, Typography } from "@mui/material";
import { Link } from "react-router-dom";

const ferments = [
  { name: "Kefir", note: "Lively milk or water grains, tangy and fizzy." },
  { name: "Kombucha", note: "Tea + SCOBY alchemy with bright acidity." },
  { name: "Sourdough", note: "Wild yeast starter for airy crumb and depth." },
  { name: "Kimchi", note: "Spicy, garlicky, crunchy fermentation staple." },
  { name: "Sauerkraut", note: "Cabbage transformed into crisp, sour crunch." },
  { name: "Miso", note: "Koji + soy for a savory umami engine." },
  { name: "Tempeh", note: "Whole-bean cake with nutty depth." },
  { name: "Kvass", note: "Lightly fermented bread or beet tonic." },
  { name: "Yogurt", note: "Gentle incubation for creamy cultures." },
  { name: "Natto", note: "Sticky soybean ferment, bold and potent." },
  { name: "Tepache", note: "Pineapple peel fizz with warm spice." },
  { name: "Pickles", note: "Brine-first crunch with garlic and dill." },
];

const pillars = [
  {
    title: "Culture Library",
    body: "Document starters, origins, flavor notes, and schedules in a shared index.",
  },
  {
    title: "Swap Board",
    body: "Offer extra SCOBYs, grains, and starter jars to neighbors and friends.",
  },
  {
    title: "Ferment Log",
    body: "Track batches, temps, tasting notes, and how each culture evolved.",
  },
  {
    title: "Knowledge Map",
    body: "Collect techniques, troubleshooting, and variations from the community.",
  },
];

export default function CultureClub() {
  return (
    <Box
      sx={{
        minHeight: "100vh",
        background:
          "radial-gradient(circle at 12% 8%, rgba(254, 240, 196, 0.9), rgba(250, 248, 235, 0.4) 35%), radial-gradient(circle at 88% 15%, rgba(187, 232, 197, 0.8), rgba(245, 251, 248, 0.1) 45%), linear-gradient(135deg, #f6f2e7, #f2f7f1 55%, #f0efe9 100%)",
        color: "var(--ink)",
        fontFamily: '"Garamond Premier Pro", "Garamond", "Palatino Linotype", serif',
        "--ink": "#2a2a2a",
        "--accent": "#c1732a",
        "--accent-2": "#3f7d5c",
        "--panel": "rgba(255, 255, 255, 0.82)",
        position: "relative",
        overflow: "hidden",
        pb: { xs: 8, md: 12 },
      }}
    >
      <Box
        sx={{
          position: "absolute",
          inset: "-20% auto auto -10%",
          width: "420px",
          height: "420px",
          borderRadius: "48% 52% 56% 44% / 55% 45% 55% 45%",
          background: "radial-gradient(circle, rgba(193, 115, 42, 0.18), transparent 70%)",
          zIndex: 0,
        }}
      />
      <Box
        sx={{
          position: "absolute",
          right: "-12%",
          top: "18%",
          width: "360px",
          height: "360px",
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(63, 125, 92, 0.25), transparent 70%)",
          zIndex: 0,
        }}
      />

      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1, pt: { xs: 8, md: 12 } }}>
        <Stack spacing={7}>
          <Stack spacing={3}>
            <Chip
              label="Culture Club"
              sx={{
                alignSelf: "flex-start",
                background: "rgba(42,42,42,0.08)",
                color: "var(--ink)",
                border: "1px solid rgba(42,42,42,0.12)",
                fontWeight: 700,
                letterSpacing: 1,
                textTransform: "uppercase",
              }}
            />
            <Typography
              variant="h2"
              sx={{
                fontFamily: '"Cormorant Garamond", "Garamond", "Palatino Linotype", serif',
                fontWeight: 700,
                letterSpacing: "-0.5px",
                lineHeight: 1.05,
                maxWidth: 760,
              }}
            >
              A warm, bubbling home for ferments, starters, and the people who keep them alive.
            </Typography>
            <Typography variant="h6" sx={{ color: "rgba(42,42,42,0.75)", maxWidth: 720 }}>
              Culture Club is a living atlas of kefir, kombucha, sourdough, kimchi, sauerkraut, and every other
              bright, funky, delicious ferment. Share your cultures, track your batches, and learn from the
              community.
            </Typography>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <Button
                variant="contained"
                size="large"
                component={Link}
                to="/"
                sx={{
                  background: "linear-gradient(120deg, #c1732a, #e39b52)",
                  color: "#1f1a14",
                  fontWeight: 800,
                  borderRadius: 999,
                  px: 3.5,
                }}
              >
                Join the club
              </Button>
              <Button
                variant="outlined"
                size="large"
                component={Link}
                to="/"
                sx={{
                  borderColor: "rgba(42,42,42,0.4)",
                  color: "var(--ink)",
                  fontWeight: 700,
                  borderRadius: 999,
                  px: 3.5,
                }}
              >
                Share a culture
              </Button>
            </Stack>
          </Stack>

          <Grid container spacing={3}>
            {pillars.map((pillar) => (
              <Grid item xs={12} sm={6} md={3} key={pillar.title}>
                <Card
                  elevation={0}
                  sx={{
                    height: "100%",
                    borderRadius: 4,
                    border: "1px solid rgba(42,42,42,0.1)",
                    background: "var(--panel)",
                    p: 2.5,
                  }}
                >
                  <Typography variant="h6" fontWeight={700} gutterBottom>
                    {pillar.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {pillar.body}
                  </Typography>
                </Card>
              </Grid>
            ))}
          </Grid>

          <Box>
            <Stack direction="row" spacing={2} alignItems="center" mb={2}>
              <Typography variant="h4" fontWeight={700}>
                Ferments to explore
              </Typography>
              <Chip
                label="Growing list"
                size="small"
                sx={{
                  background: "rgba(63,125,92,0.15)",
                  color: "#2f5f46",
                  fontWeight: 700,
                }}
              />
            </Stack>
            <Grid container spacing={2}>
              {ferments.map((ferment) => (
                <Grid item xs={12} sm={6} md={4} key={ferment.name}>
                  <Box
                    sx={{
                      borderRadius: 3,
                      border: "1px solid rgba(42,42,42,0.12)",
                      background: "rgba(255,255,255,0.7)",
                      p: 2,
                      height: "100%",
                    }}
                  >
                    <Typography variant="subtitle1" fontWeight={700}>
                      {ferment.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {ferment.note}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>

          <Box
            sx={{
              borderRadius: 4,
              border: "1px solid rgba(42,42,42,0.1)",
              background: "rgba(255,255,255,0.75)",
              p: { xs: 3, md: 4 },
            }}
          >
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} md={7}>
                <Typography variant="h4" fontWeight={700} gutterBottom>
                  Ready to trade a starter?
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  List what you have, what you want, and the notes that keep your culture thriving. We’ll make
                  the matching later — for now, this is the vision board.
                </Typography>
              </Grid>
              <Grid item xs={12} md={5}>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                  <Button
                    variant="contained"
                    size="large"
                    component={Link}
                    to="/"
                    sx={{
                      background: "linear-gradient(120deg, #3f7d5c, #65b68a)",
                      color: "#0b1a12",
                      fontWeight: 800,
                      borderRadius: 999,
                    }}
                  >
                    Post a starter
                  </Button>
                  <Button
                    variant="outlined"
                    size="large"
                    component={Link}
                    to="/"
                    sx={{
                      borderColor: "rgba(63,125,92,0.5)",
                      color: "#2f5f46",
                      fontWeight: 700,
                      borderRadius: 999,
                    }}
                  >
                    Browse cultures
                  </Button>
                </Stack>
              </Grid>
            </Grid>
          </Box>
        </Stack>
      </Container>
    </Box>
  );
}
